import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "./ui/Dialog";
import { Button } from "./ui/Button";
import { Check, Crown } from "lucide-react";
import { useUpgradeFlow } from "../hooks/useUpgradeFlow";
import { useAuth } from "../contexts/AuthContext";
import { getPrices } from "../services/billing";
import { toast } from "./ui/Toast";
import type { PriceItem } from "../types/billing";
import { PREMIUM_FEATURES } from "../constants/premiumFeatures";
import {
  FALLBACK_PREMIUM_CURRENCY,
  FALLBACK_PREMIUM_PRICE_GBP,
  FALLBACK_PREMIUM_PRICE_USD,
} from "../constants/fallbackPricing";

const CURRENCY_PREFERENCE_KEY = "planmymeals_currency_preference";

export interface PaywallModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPremium: boolean;
}

type Currency = "gbp" | "usd";
type Interval = "month" | "year";

const _currencySymbol = (currency: Currency): string =>
  currency === "gbp" ? "£" : "$";

const _formatDisplayPrice = (
  currency: Currency,
  unitAmount: number,
  interval: Interval,
): string => {
  const symbol = _currencySymbol(currency);
  if (unitAmount <= 0) {
    return interval === "year" ? `${symbol}?/year` : `${symbol}?/month`;
  }
  const major = (unitAmount / 100).toFixed(2);
  return interval === "year"
    ? `${symbol}${major}/year`
    : `${symbol}${major}/month`;
};

const PaywallModal: React.FC<PaywallModalProps> = ({
  open,
  onOpenChange,
  isPremium,
}) => {
  const {
    startUpgrade,
    startManageBilling,
    loading,
    managingBilling,
    error,
    paymentUnavailable,
  } = useUpgradeFlow();

  const { isGuest } = useAuth();

  const [prices, setPrices] = useState<PriceItem[]>([]);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [currency, setCurrency] = useState<Currency>(() => {
    try {
      const saved = localStorage.getItem(CURRENCY_PREFERENCE_KEY) as Currency;
      if (saved === "gbp" || saved === "usd") return saved;
    } catch {
      // ignore localStorage errors
    }
    return "gbp";
  });
  const [interval, setInterval] = useState<Interval>("month");

  // Load prices when the modal opens
  useEffect(() => {
    if (!open || isPremium) return;
    let cancelled = false;
    setPricesLoading(true);
    setPrices([]);
    getPrices()
      .then((res) => {
        if (!cancelled) setPrices(res.prices);
      })
      .catch(() => {
        if (!cancelled) setPrices([]);
      })
      .finally(() => {
        if (!cancelled) setPricesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isPremium]);

  // Persist currency preference
  useEffect(() => {
    try {
      localStorage.setItem(CURRENCY_PREFERENCE_KEY, currency);
    } catch {
      // ignore
    }
  }, [currency]);

  // Surface hook errors as toasts
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const selectedPrice = useMemo(() => {
    return prices.find(
      (p) => p.currency === currency && p.interval === interval,
    );
  }, [prices, currency, interval]);

  const monthlyPrice = useMemo(() => {
    return prices.find(
      (p) => p.currency === currency && p.interval === "month",
    );
  }, [prices, currency]);

  const annualPrice = useMemo(() => {
    return prices.find((p) => p.currency === currency && p.interval === "year");
  }, [prices, currency]);

  const billingAvailable = prices.length > 0;

  const canShowToggles = !isPremium && billingAvailable;

  const handlePrimaryAction = async () => {
    if (isPremium) {
      await startManageBilling();
      return;
    }
    // Guest accounts must register before they can upgrade.
    // Close the modal first so it doesn't linger after navigation.
    if (isGuest) {
      onOpenChange(false);
    }
    await startUpgrade({
      currency,
      interval,
    });
  };

  const primaryButtonText = isPremium
    ? "Manage billing"
    : isGuest
    ? "Register to Upgrade"
    : "Upgrade to Premium";

  const primaryLoading = isPremium ? managingBilling : loading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="paywall-modal">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Crown
              className="h-5 w-5 text-warning-500"
              aria-hidden="true"
              data-testid="paywall-crown-icon"
            />
            <DialogTitle>Premium Features</DialogTitle>
          </div>
          <DialogDescription>
            {isPremium
              ? "Manage your premium subscription."
              : "Upgrade to Premium to unlock the full power of SevenServings."}
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-2 py-2">
          {PREMIUM_FEATURES.map((feature) => (
            <li
              key={feature}
              className="flex items-start gap-2 text-sm text-secondary-700"
            >
              <Check
                className="h-4 w-4 text-success-500 mt-0.5 flex-shrink-0"
                aria-hidden="true"
              />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {canShowToggles && (
          <div className="space-y-3">
            {/* Currency toggle */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-secondary-500 uppercase tracking-wide">
                Currency
              </span>
              <div className="flex gap-1">
                {(["gbp", "usd"] as Currency[]).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCurrency(c)}
                    className={`px-2.5 py-1 rounded-md text-sm font-medium transition-colors ${
                      currency === c
                        ? "bg-primary-500 text-white"
                        : "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
                    }`}
                    data-testid={`currency-toggle-${c}`}
                  >
                    {c === "gbp" ? "GBP £" : "USD $"}
                    {c === "gbp" && (
                      <span className="ml-1 text-xs opacity-80">
                        Your currency
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Interval toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <span className="text-xs font-medium text-secondary-500 uppercase tracking-wide">
                Billing
              </span>
              <div className="flex gap-2 flex-1">
                {(["month", "year"] as Interval[]).map((i) => {
                  const price = i === "month" ? monthlyPrice : annualPrice;
                  const isSelected = interval === i;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setInterval(i)}
                      className={`flex-1 px-3 py-2 rounded-lg border text-left transition-all ${
                        isSelected
                          ? "bg-primary-50 border-primary-300 shadow-sm"
                          : "bg-white border-secondary-200 hover:border-primary-300"
                      }`}
                      data-testid={`interval-toggle-${i}`}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-sm font-semibold ${
                            isSelected
                              ? "text-primary-900"
                              : "text-secondary-900"
                          }`}
                        >
                          {i === "month" ? "Monthly" : "Annual"}
                        </span>
                        {i === "year" &&
                          price?.savings_vs_monthly_pct != null && (
                            <span className="text-xs font-medium bg-success-100 text-success-700 px-1.5 py-0.5 rounded">
                              Save {price.savings_vs_monthly_pct}%
                            </span>
                          )}
                      </div>
                      <p className="text-xs text-secondary-500 mt-0.5">
                        {price && price.unit_amount > 0
                          ? _formatDisplayPrice(currency, price.unit_amount, i)
                          : i === "year"
                          ? `${_currencySymbol(currency)}?/year`
                          : `${_currencySymbol(currency)}?/month`}
                      </p>
                      {i === "year" &&
                        monthlyPrice &&
                        annualPrice &&
                        annualPrice.unit_amount > 0 &&
                        monthlyPrice.unit_amount > 0 && (
                          <p className="text-xs text-secondary-500 mt-0.5">
                            (
                            {_formatDisplayPrice(
                              currency,
                              Math.round(annualPrice.unit_amount / 12),
                              "month",
                            )}{" "}
                            equivalent)
                          </p>
                        )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {pricesLoading && (
          <p
            className="text-sm text-secondary-500"
            data-testid="prices-loading"
          >
            Loading prices…
          </p>
        )}

        {!isPremium && !billingAvailable && !pricesLoading && (
          <div
            className="rounded-lg border border-warning-200 bg-warning-50 p-3"
            data-testid="paywall-fallback-pricing"
          >
            <p className="text-sm font-medium text-warning-900">
              Premium plans start at{" "}
              {FALLBACK_PREMIUM_CURRENCY === "gbp" ? "£" : "$"}
              {FALLBACK_PREMIUM_CURRENCY === "gbp"
                ? FALLBACK_PREMIUM_PRICE_GBP.toFixed(2)
                : FALLBACK_PREMIUM_PRICE_USD.toFixed(2)}
              /month
            </p>
            <p className="text-xs text-warning-700 mt-1">
              Billing is not configured on this server yet. Upgrade will be
              enabled once payments are set up.
            </p>
          </div>
        )}

        {paymentUnavailable && billingAvailable && (
          <p
            className="text-sm text-danger-600"
            role="alert"
            data-testid="paywall-error"
          >
            Billing is not configured on this server.
          </p>
        )}

        <DialogFooter>
          <Button
            onClick={handlePrimaryAction}
            isLoading={primaryLoading}
            disabled={!isPremium && !billingAvailable}
            title={
              !isPremium && !billingAvailable
                ? "Billing is not configured on this server"
                : undefined
            }
            data-testid={isPremium ? "manage-billing-button" : "upgrade-button"}
          >
            {primaryButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaywallModal;
